import {useId} from 'react';

import {useSkeletonReveal} from '../../hooks';
import {cn} from '../../lib/utils';
import {Field, FieldSkeleton} from '../Field';
import {Textarea} from '../shadcn/textarea';

import type {FieldProps} from '../Field';
import type {ComponentProps, ReactNode} from 'react';

export interface FieldTextareaProps extends ComponentProps<'textarea'> {
  label?: string;
  description?: string;
  error?: string;
  orientation?: FieldProps['orientation'];
  resizeX?: boolean;
  resizeY?: boolean;
  badge?: ReactNode;
  /** Corner badge rendered opposite the label — e.g. an AI generating/filled indicator. */
  cornerBadge?: ReactNode;
  /** Shows a pulsing skeleton in place of the value while it's being loaded/generated. */
  isLoading?: boolean;
}

export function FieldTextarea({
  label,
  description,
  error,
  orientation,
  id,
  className,
  resizeX = false,
  resizeY = false,
  badge,
  cornerBadge,
  isLoading,
  onFocus,
  ...textareaProps
}: FieldTextareaProps) {
  const generatedId = useId();
  const resolvedId = id ?? generatedId;
  const hasError = !!error;
  const {showSkeleton, onFocus: onRevealFocus} = useSkeletonReveal(isLoading);

  const resizeClass =
    resizeX && resizeY ? 'resize' : resizeX ? 'resize-x' : resizeY ? 'resize-y' : 'resize-none';

  return (
    <Field
      label={label}
      description={description}
      error={error}
      htmlFor={resolvedId}
      orientation={orientation}
      cornerBadge={cornerBadge}
    >
      <div className="relative">
        {badge && <span className="absolute inset-e-2 top-2">{badge}</span>}
        {showSkeleton && <FieldSkeleton className="inset-s-3 top-3" />}
        <Textarea
          id={resolvedId}
          aria-invalid={hasError || undefined}
          onFocus={e => {
            onRevealFocus();
            onFocus?.(e);
          }}
          className={cn(
            resizeClass,
            badge && 'pe-14',
            showSkeleton && 'placeholder:opacity-0',
            className
          )}
          {...textareaProps}
        />
      </div>
    </Field>
  );
}
